import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useT } from '../../i18n/LangProvider';

type Opt = { id: string; labelEn: string; labelFr: string };
type Question = { id: string; type: 'text' | 'choice'; promptEn: string; promptFr: string; options: Opt[] };
type Response = { email: string; answers: Record<string, string>; submittedAt: string };

async function handle<T>(res: Response | globalThis.Response): Promise<T> {
  const r = res as globalThis.Response;
  if (!r.ok) {
    let msg = `Error ${r.status}`;
    try { const j = await r.json(); msg = j.error || msg; } catch { /* noop */ }
    throw new Error(msg);
  }
  return r.json();
}

export default function PollPage() {
  const { t, lang } = useT();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [existing, setExisting] = useState<Response | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const prompt = (q: Question) => (lang === 'fr' ? q.promptFr : q.promptEn) || q.promptEn;
  const lbl = (o: Opt) => (lang === 'fr' ? o.labelFr : o.labelEn) || o.labelEn;

  useEffect(() => {
    (async () => {
      try {
        const [cfg, my] = await Promise.all([
          handle<{ questions: Question[] }>(await fetch('/api/poll-config')),
          handle<{ response: Response | null }>(await fetch('/api/poll-my'))
        ]);
        setQuestions(cfg.questions || []);
        setExisting(my.response);
      } catch (e) {
        const msg = (e as Error).message;
        if (msg.includes('401')) { window.location.href = '/.auth/login/aad?post_login_redirect_uri=/poll'; return; }
        setError(msg);
      } finally { setLoading(false); }
    })();
  }, []);

  const submit = async () => {
    for (const q of questions) {
      if (!answers[q.id] || !answers[q.id].trim()) { setError(t('poll.required')); return; }
    }
    setSubmitting(true); setError(null);
    try {
      const r = await handle<{ response: Response }>(await fetch('/api/poll-submit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ answers })
      }));
      setExisting(r.response); setDone(true);
    } catch (e) { setError((e as Error).message); } finally { setSubmitting(false); }
  };

  const FIELD = 'w-full bg-bg-base border border-white/[0.12] rounded-xl px-3 py-2.5 text-sm text-ink outline-none focus:border-brand-red';
  const LABEL = 'text-ink font-display text-lg mb-3 block';

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 pt-36 pb-32">
      <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-xs tracking-[0.25em] uppercase text-brand-red mb-5 font-mono">{t('poll.kicker')}</motion.p>
      <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="font-display text-5xl md:text-7xl tracking-tight text-ink">{t('poll.title')}</motion.h1>
      <p className="text-ink-muted mt-5 max-w-md text-lg">{t('poll.lead')}</p>

      {loading && <p className="text-ink-muted mt-10">…</p>}

      {!loading && existing && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-10 card p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 text-sm">✓</span>
            <h2 className="font-display text-2xl text-ink">{t('poll.submitted')}</h2>
          </div>
          <p className="text-xs text-ink-faint">{t('poll.submittedNote')}</p>
        </motion.div>
      )}

      {!loading && !existing && questions.length === 0 && (
        <p className="text-ink-muted mt-10">{lang === 'fr' ? 'Le sondage sera bientôt disponible.' : 'The poll will be available soon.'}</p>
      )}

      {!loading && !existing && questions.length > 0 && (
        <div className="mt-10 space-y-8">
          {questions.map((q) => (
            <div key={q.id}>
              <span className={LABEL}>{prompt(q)}</span>
              {q.type === 'text' ? (
                <input className={FIELD} value={answers[q.id] || ''} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} />
              ) : (
                <div className="space-y-2">
                  {q.options.map((o) => {
                    const on = answers[q.id] === o.id;
                    return (
                      <button key={o.id} type="button" onClick={() => setAnswers({ ...answers, [q.id]: o.id })}
                        className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all ${on ? 'bg-brand-red/[0.08] border-brand-red' : 'bg-bg-base border-white/[0.08] hover:border-white/25'}`}>
                        <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs ${on ? 'bg-brand-red text-white' : 'border-2 border-ink-faint'}`}>{on && '✓'}</span>
                        <span className="text-ink text-sm">{lbl(o)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          <button onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 bg-brand-red text-white text-sm rounded-md px-6 py-3 disabled:opacity-40 hover:bg-brand-red-soft transition-colors">
            {submitting ? t('poll.submitting') : t('poll.submit')}
          </button>
        </div>
      )}

      {error && <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 p-4 rounded-xl bg-red-950/50 border border-red-700/50 text-red-300 text-sm">{error}</motion.p>}
      {done && <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 p-4 rounded-xl bg-emerald-950/50 border border-emerald-700/50 text-emerald-300 text-sm">{t('poll.success')}</motion.p>}
    </div>
  );
}
